import React, { useState, useEffect } from 'react';
import { api } from '@/lib/api';
import { useBranding } from '@/lib/branding';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ImageUploader, fileUrl } from '@/components/Uploaders';
import { Palette, RefreshCw, Save, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function BrandingSettingsPage() {
  const branding = useBranding();
  const [form, setForm] = useState({
    logo_url: '', favicon_url: '',
    app_name: 'PROAKTIF', institution_name: 'Pemerintah Kota Makassar',
    institution_sub: 'Bagian Protokol dan Komunikasi Pimpinan',
    primary_color: '#DC2626', accent_color: '#FBBF24',
    motto: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (branding) {
      setForm({
        logo_url: branding.logo_url || '',
        favicon_url: branding.favicon_url || '',
        app_name: branding.app_name || 'PROAKTIF',
        institution_name: branding.institution_name || '',
        institution_sub: branding.institution_sub || '',
        primary_color: branding.primary_color || '#DC2626',
        accent_color: branding.accent_color || '#FBBF24',
        motto: branding.motto || '',
      });
    }
  }, [branding?.logo_url]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setSaving(true);
    try {
      await api.patch('/branding', form);
      await branding.reload();
      toast.success('Branding berhasil diperbarui');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal');
    } finally {
      setSaving(false);
    }
  };

  const resetDefault = async () => {
    if (!window.confirm('Kembalikan branding ke default?')) return;
    const def = {
      logo_url: '/logo-pemkot-makassar.png',
      favicon_url: '/favicon.png',
      app_name: 'PROAKTIF',
      institution_name: 'Pemerintah Kota Makassar',
      institution_sub: 'Bagian Protokol dan Komunikasi Pimpinan',
      primary_color: '#DC2626',
      accent_color: '#FBBF24',
      motto: 'Sekali Layar Terkembang, Pantang Biduk Surut ke Pantai',
    };
    setForm(def);
    await api.patch('/branding', def);
    await branding.reload();
    toast.success('Branding direset ke default');
  };

  return (
    <div className="space-y-6 max-w-5xl">
      <PageHeader
        title="Pengaturan Branding & Logo"
        subtitle="Kelola identitas visual aplikasi PROAKTIF. Perubahan akan langsung tampil di seluruh sistem."
        breadcrumb={[{ label: 'Beranda', to: '/dashboard' }, { label: 'Pengaturan' }, { label: 'Branding' }]}
        action={<Button variant="outline" onClick={resetDefault}><RefreshCw className="w-4 h-4 mr-2" />Reset Default</Button>}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 border-slate-200 shadow-premium">
          <CardHeader><CardTitle className="text-base font-bold">Identitas Aplikasi</CardTitle></CardHeader>
          <CardContent className="space-y-5">
            <div>
              <Label className="mb-2 block">Logo Utama</Label>
              <ImageUploader
                value={form.logo_url}
                onChange={(url) => setForm({ ...form, logo_url: url })}
                endpoint="/upload/branding/logo"
                label="Upload Logo"
                size={120}
                testid="upload-logo"
              />
              <p className="text-[11px] text-slate-500 mt-2">Tampil di Login, Sidebar, dan Header laporan. Format PNG transparan disarankan, ukuran minimum 256x256px.</p>
            </div>

            <div>
              <Label className="mb-2 block">Favicon</Label>
              <ImageUploader
                value={form.favicon_url}
                onChange={(url) => setForm({ ...form, favicon_url: url })}
                endpoint="/upload/branding/logo"
                label="Upload Favicon"
                size={64}
                testid="upload-favicon"
              />
              <p className="text-[11px] text-slate-500 mt-2">Ikon yang tampil di tab browser. Format PNG/ICO, ukuran 32x32 atau 64x64px.</p>
            </div>

            <div className="grid md:grid-cols-2 gap-3">
              <div><Label>Nama Aplikasi</Label><Input value={form.app_name} onChange={(e) => setForm({ ...form, app_name: e.target.value })} /></div>
              <div><Label>Motto</Label><Input value={form.motto} onChange={(e) => setForm({ ...form, motto: e.target.value })} /></div>
              <div className="md:col-span-2"><Label>Nama Institusi</Label><Input value={form.institution_name} onChange={(e) => setForm({ ...form, institution_name: e.target.value })} /></div>
              <div className="md:col-span-2"><Label>Sub Institusi</Label><Input value={form.institution_sub} onChange={(e) => setForm({ ...form, institution_sub: e.target.value })} /></div>
              <div>
                <Label>Warna Utama</Label>
                <div className="flex items-center gap-2">
                  <input type="color" value={form.primary_color} onChange={(e) => setForm({ ...form, primary_color: e.target.value })} className="w-12 h-10 rounded border border-slate-200" />
                  <Input value={form.primary_color} onChange={(e) => setForm({ ...form, primary_color: e.target.value })} className="font-mono" />
                </div>
              </div>
              <div>
                <Label>Warna Aksen</Label>
                <div className="flex items-center gap-2">
                  <input type="color" value={form.accent_color} onChange={(e) => setForm({ ...form, accent_color: e.target.value })} className="w-12 h-10 rounded border border-slate-200" />
                  <Input value={form.accent_color} onChange={(e) => setForm({ ...form, accent_color: e.target.value })} className="font-mono" />
                </div>
              </div>
            </div>

            <div className="pt-4 flex justify-end">
              <Button onClick={save} disabled={saving} data-testid="save-branding" className="bg-[#DC2626] hover:bg-[#B91C1C]">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Simpan Perubahan
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-premium h-fit">
          <CardHeader><CardTitle className="text-base font-bold flex items-center gap-2"><Palette className="w-4 h-4 text-[#DC2626]" />Preview</CardTitle></CardHeader>
          <CardContent>
            <div className="rounded-xl border border-slate-200 overflow-hidden">
              <div className="hero-warm p-5 text-center">
                {form.logo_url && <img src={fileUrl(form.logo_url)} alt="preview" className="w-20 h-20 mx-auto object-contain" />}
                <div className="mt-3 text-xs uppercase tracking-widest text-slate-500 font-semibold">{form.institution_name}</div>
                <div className="text-2xl font-extrabold mt-1" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                  PRO<span style={{ color: form.primary_color }}>{(form.app_name || 'AKTIF').replace('PRO', '')}</span>
                </div>
                <div className="text-[10px] text-slate-500 mt-2">{form.institution_sub}</div>
                <div className="mt-3 flex items-center justify-center gap-2">
                  <span className="w-12 h-2 rounded-full" style={{ background: form.primary_color }} />
                  <span className="w-12 h-2 rounded-full" style={{ background: form.accent_color }} />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
